import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import { DataGrid, GridToolbarContainer, GridToolbarColumnsButton, GridToolbarFilterButton, GridToolbarExport, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { CircularProgress } from '@mui/material';
import '../../Modal.css';
import DatabaseService from "../../services/database.service.js";
import { convertDateToString } from '../../common/ConvertTools.js';
import gridSxStyle from "../../common/GridSxStyles.js"
import DeviceModalMessageEdit from './DeviceModalMessageEdit';
import { ToastContainer, toast } from 'react-toastify';

ReactModal.setAppElement('#root');

const DeviceModalMessageView = ({ isOpen, onRequestClose, onMessageDelete, selectedDevice, isDesktop, isAdmin, isPartner, onUpdateDeviceMsgs }) => {
  const [messages, setMessages] = useState([]);
  const [isModalMsgEditOpen, setModalMsgEditOpen] = useState(false);

  const updateMessages = (messages) => {
    setMessages(
      messages.map((msg, index) => ({
        id: index,
        id_msg: msg.id_msg,
        date_viewed: msg.date_viewed,
        date_push: convertDateToString(msg.date_push),
        message: msg.msg,
        fw_version_min: msg.fw_version_min,
        fw_version_max: msg.fw_version_max,
        criticity: msg.criticity
      }))
    );
  };

  useEffect(() => {
    if (selectedDevice && selectedDevice.msg_devices_push) {
      updateMessages(selectedDevice.msg_devices_push);
    }
  }, [selectedDevice, isDesktop]);

  const columns = [
    {
      width: 40, // Fixing the width to a number instead of a string
      resizable: false,
      renderCell: ({ row }) => (
        <div style={{ textAlign: 'center' }}>
          <i className="fa fa-trash" style={{ padding: "5px" }} onClick={async () => {
            const ret = await onMessageDelete(row.id_msg);
            if (ret > 0) {
              toast.success("Message deleted successfully", {
                position: "bottom-right",
                autoClose: 2000,
                style: {
                  zIndex: 50
                }
              });

              const updatedMessages = selectedDevice.msg_devices_push.filter(msg => msg.id_msg !== row.id_msg);
              selectedDevice.msg_devices_push = updatedMessages;
              updateMessages(updatedMessages);
              onUpdateDeviceMsgs(selectedDevice);
            } else {
              toast.error("Error deleting the message", {
                position: "bottom-right",
                autoClose: 2000,
                style: {
                  zIndex: 50
                }
              });
            }
          }}></i>
        </div>
      )
    },
    { field: 'date_push', headerName: 'Pushed', maxWidth: 170 },
    {
      field: 'date_viewed', headerName: 'Viewed', maxWidth: 170, editable: true,
      renderCell: ({ row }) => (
        <div>
          {(row.date_viewed != '0' && row.date_viewed != null) ? convertDateToString(row.date_viewed) : "unseen"}
        </div>
      )
    },
    { field: 'message', headerName: 'Message', flex: 1, editable: true, minWidth: 200 }, // Ensure message has a minimum width
    { field: 'fw_version_min', headerName: 'Min Firmware', maxWidth: 100, editable: true },
    { field: 'fw_version_max', headerName: 'Max Firmware', maxWidth: 100, editable: true },
    {
      field: 'criticity', headerName: 'Criticity', maxWidth: 100, editable: true,
      renderCell: ({ row }) => (
        <div>
          {(row.criticity == 0 || row.criticity == null) ? "normal" : "critical"}
        </div>
      )
    }
  ];

  const handleCreateMsg = ({ fwMin, fwMax, msg, criticity }) => {
    DatabaseService.createMsg(selectedDevice.mac_custom, selectedDevice.user_id, msg, fwMin, fwMax, criticity)
      .then(response => {
        console.log("Message successfully saved:", response.data);
        toast.success("Message sent successfully", {
          position: "bottom-right",
          autoClose: 2000
        });

        const updatedMessages = [...selectedDevice.msg_devices_push, {
          id_msg: response.data[0].id_msg,
          owner_email: selectedDevice.owner_email,
          msg: msg,
          fw_version_min: fwMin,
          fw_version_max: fwMax,
          mac_custom: selectedDevice.mac_custom,
          date_push: (Date.now() / 1000).toFixed(0),
          criticity: criticity
        }];
        selectedDevice.msg_devices_push = updatedMessages;
        updateMessages(updatedMessages);
        onUpdateDeviceMsgs(selectedDevice);
      })
      .catch(error => {
        console.error("Failed to save message:", error);
        toast.error("Message sending failed: " + error, {
          position: "bottom-right",
          autoClose: 2000
        });
      });
  };

  const processRowUpdate = async (newRow) => {
    try {
      const response = await DatabaseService.editMsg(
        newRow.id_msg,
        selectedDevice.mac_custom,
        selectedDevice.owner_email,
        newRow.message,
        Number(newRow.fw_version_min),
        Number(newRow.fw_version_max),
        (newRow.date_viewed == 0) ? null : (Date.now() / 1000).toFixed(0),
        newRow.criticity
      );

      if (response.status === 204) {
        toast.success("Message edited successfully", {
          position: "bottom-right",
          autoClose: 2000
        });

        const updatedMessages = selectedDevice.msg_devices_push.map((item) => {
          if (item.id_msg === newRow.id_msg) {
            return {
              ...item,
              msg: newRow.message,
              fw_version_min: newRow.fw_version_min,
              fw_version_max: newRow.fw_version_max,
              date_viewed: (newRow.date_viewed == 0) ? null : newRow.date_viewed,
              criticity: newRow.criticity
            };
          }
          return item;
        });

        selectedDevice.msg_devices_push = updatedMessages;
        updateMessages(updatedMessages);
        onUpdateDeviceMsgs(selectedDevice);
      } else {
        toast.error("Error editing the message", {
          position: "bottom-right",
          autoClose: 2000
        });
      }
      return newRow;
    } catch (error) {
      console.error("Error updating the message:", error);
      toast.error("Error editing the message", {
        position: "bottom-right",
        autoClose: 2000
      });
    }
  };

  const TableToolbar = () => (
    <GridToolbarContainer>
      {isDesktop && (
        <>
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <GridToolbarExport />
        </>
      )}
      <GridToolbarQuickFilter debounceMs={700} />
    </GridToolbarContainer>
  );

  return (
    <>
      <ReactModal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        contentLabel="Device Messages"
        className="modal-content"
        overlayClassName="modal-overlay"
        style={{
          overlay: {
            backgroundColor: 'rgba(50, 50, 50, 0.75)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          },
          content: {
            display: 'flex',
            flexDirection: 'column',
            height: '95%',
            width: '95%',
            maxWidth: '95vh',
            maxHeight: '95vh',
            margin: 'auto',
            overflow: 'hidden',
            borderRadius: '8px',
          }
        }}
      >
        <ToastContainer />
        <div className="modal-header">
          <h5 className="modal-title">Messages - Device {selectedDevice.ble_name} ({selectedDevice.mac_custom}) - FW {selectedDevice.version} - ML {selectedDevice.maintenanceLevel}</h5>
          <div style={{ display: 'flex', marginLeft: 'auto' }}>
            <button className="btn btn-outline-primary m-1" onClick={() => setModalMsgEditOpen(true)} style={{ marginRight: '10px' }}>
              <i className="fas fa-edit"></i>
            </button>
            <button className="btn btn-outline-primary m-1" onClick={onRequestClose} style={{ marginRight: '10px' }}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div style={{ height: 'calc(100vh - 150px)', width: '100%', overflow: 'auto' }}>
          <DataGrid
            getRowId={(row) => row.id_msg ? row.id_msg : "unused"}
            rows={messages}
            columns={columns}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={() => { }}
            pageSizeOptions={[15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 100]}
            disableSelectionOnClick
            getRowHeight={() => 'auto'}
            disableDensitySelector
            slots={{ toolbar: TableToolbar }}
            sx={{
              ...gridSxStyle,
              '& .MuiDataGrid-root': {
                overflowX: 'auto', // Ensure the grid itself is scrollable horizontally
              },
            }}
          />
        </div>
      </ReactModal>

      {((isAdmin || isPartner) && isModalMsgEditOpen) && (
        <DeviceModalMessageEdit
          isOpen={isModalMsgEditOpen}
          onRequestClose={() => setModalMsgEditOpen(false)}
          handleCreateMsg={handleCreateMsg}
          selectedDevice={selectedDevice}
        />
      )}
    </>
  );
};

export default DeviceModalMessageView;
